import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  WritableSignal,
  effect,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BANK_SELECTOR_FETCH_SUBSTITUTES,
  BANK_SELECTOR_SUBSTITUTE_BANK,
  BankSelectorActions,
  BankState,
  RFQ_PROVIDER_STATE_UNQUOTED,
  RfqQuoteState
} from '@approot/models';
import { dvSchemas } from '@approot/utilities';
import { clone, cloneDeep } from 'lodash';

type Bank = dvSchemas['ProviderSummaryDTO'];

type BankStatus = {
  selected: boolean;
  status?: RfqQuoteState;
};

@Component({
  selector: 'app-banks-selector',
  templateUrl: './banks-selector.component.html',
  styleUrls: ['./banks-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BanksSelectorComponent),
      multi: true
    }
  ]
})
export class BanksSelectorComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('bankElement', { static: false }) public bankElement: ElementRef;
  @Input() public availableBanks: Bank[] = [];
  @Input() public readonly = true;
  @Input() public availableSubstitutes: string[] = [];
  @Input() public selectedBankStates: WritableSignal<BankState[]>;
  @Output() public communicationLine = new EventEmitter<BankSelectorActions>();
  public widthOfBankElement: number;
  public banks: Record<string, BankStatus> = {};
  public onChange: (banks: Bank[]) => void = () => {};
  public value: any;
  public onTouched: any;
  public disabled: any;
  public isOpened = false;
  public bankToReplace: string;
  public selectedBankStatesValue: BankState[] = [];

  constructor() {
    effect(_ => {
      const previousSelectedBanksStates = clone(this.selectedBankStatesValue);
      this.selectedBankStatesValue = this.selectedBankStates();
      this.selectedBankStatesChanges(
        previousSelectedBanksStates,
        this.selectedBankStatesValue
      );
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableBanks?.currentValue) {
      this.availableBanksChanges(changes.availableBanks.currentValue);
    }

    this.onChange(this.providersToSend());
    this.storeWidthSize();
  }

  public providerClicked(provider: string): void {
    const providerObj = this.selectedBankStates().find(
      bank => bank.shortName === provider
    );
    this.availableSubstitutes = [];

    if (providerObj?.quoteId) {
      // if the provider is declined
      this.bankToReplace = provider;
      this.communicationLine.emit({
        command: BANK_SELECTOR_FETCH_SUBSTITUTES,
        data: providerObj.id
      });
    }
  }

  public replaceProvider(providerToReplaceWith: string): void {
    // send a message to replace a provider
    this.communicationLine.emit({
      command: BANK_SELECTOR_SUBSTITUTE_BANK,
      data: { replacee: this.bankToReplace, substitute: providerToReplaceWith }
    });
    this.bankToReplace = null;
    this.availableSubstitutes = [];
  }

  public checkboxChange(checked: boolean, bank: string): void {
    if (checked) {
      const bankFromAvailable = this.availableBanks.find(
        bankObj => bankObj.shortName === bank
      );
      if (!!bankFromAvailable) {
        this.banks[bank].selected = checked;
        this.selectedBankStates.update(state => {
          state.push({
            ...bankFromAvailable,
            quoteId: null,
            status: RFQ_PROVIDER_STATE_UNQUOTED
          });
          return state;
        });
      }
    } else {
      // when the checkbox is unchecked unselect it and reset the status
      this.banks[bank].selected = checked;
      this.banks[bank].status = RFQ_PROVIDER_STATE_UNQUOTED;

      const indexToRemove = this.selectedBankStates().findIndex(
        bankObj => bankObj.shortName === bank
      );

      if (indexToRemove > -1) {
        this.selectedBankStates.update(state => {
          state.splice(indexToRemove, 1);
          return state;
        });
      }
    }
    this.onChange(this.providersToSend());
  }

  public selectorOpenChange(isSelectorOpened: boolean): void {
    if (!isSelectorOpened) {
      this.bankToReplace = null;
      this.availableSubstitutes = [];
    }
  }

  public writeValue(value: any) {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn; // we save a reference to the callback function  that is passed by the from control form
    // we will trigger it every time there is a change in the selected value
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // PRIVATE METHODS

  private selectedBankStatesChanges(
    previousBanks: Bank[],
    currentBanks: Bank[]
  ): void {
    // when the selectedBanks states have changed we need to de-select removed banks and select newly added banks.
    previousBanks?.forEach(prevBank => {
      const isBankCurrentlySelected = currentBanks.find(
        (curBank: Bank) => curBank.shortName === prevBank.shortName
      );
      if (!isBankCurrentlySelected && this.banks[prevBank.shortName]) {
        this.banks[prevBank.shortName].selected = false;
      }
    });

    currentBanks?.forEach(currentBank => {
      this.banks[currentBank.shortName].selected = true;
    });
    this.onChange(this.providersToSend());
  }

  private availableBanksChanges(availableBanks: Bank[]): void {
    Object.keys(this.banks).forEach(bank => {
      if (
        !availableBanks.find(availableBank => availableBank.shortName === bank)
      ) {
        delete this.banks[bank];
      }
    });

    availableBanks.forEach((bank: Bank) => {
      if (!this.banks[bank.shortName]) {
        this.banks[bank.shortName] = { selected: false };
      }
    });
  }

  private providersToSend(): Bank[] {
    return cloneDeep(this.selectedBankStates()).map(bankState => {
      return {
        id: bankState.id,
        name: bankState.name,
        shortName: bankState.shortName
      };
    });
  }

  private storeWidthSize(): void {
    setTimeout(() => {
      if (this.bankElement?.nativeElement) {
        this.widthOfBankElement = this.bankElement.nativeElement.offsetWidth;
      }
    });
  }
}
